import type { LogContext, LogEntry, Logger, LoggerConfig, LogTransport } from './types'
import { LogLevel } from './types'

/**
 * Main logger implementation
 */
class LoggerImpl implements Logger {
  private config: LoggerConfig
  private transports: LogTransport[]

  constructor(config: LoggerConfig) {
    this.config = config
    this.transports = config.transports
  }

  /**
   * Set the user ID for enriching log entries
   */
  private getUserId(): string | undefined {
    // Will be populated from app store in the future
    return undefined
  }

  /**
   * Create a base log entry with common properties
   */
  private createBaseEntry(level: LogLevel, message: string, context?: LogContext): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      environment: this.config.environment,
      userId: this.getUserId(),
      userAgent: navigator.userAgent,
    }
  }

  /**
   * Send log entry to all configured transports
   */
  private log(entry: LogEntry): void {
    this.transports.forEach((transport) => {
      try {
        transport.log(entry)
      } catch (error) {
        // Prevent transport errors from breaking the application
        console.error('Logger transport error:', error)
      }
    })
  }

  debug(message: string, context?: LogContext): void {
    const entry = this.createBaseEntry(LogLevel.DEBUG, message, context)
    this.log(entry)
  }

  info(message: string, context?: LogContext): void {
    const entry = this.createBaseEntry(LogLevel.INFO, message, context)
    this.log(entry)
  }

  warn(message: string, context?: LogContext): void {
    const entry = this.createBaseEntry(LogLevel.WARN, message, context)
    this.log(entry)
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const entry = {
      ...this.createBaseEntry(LogLevel.ERROR, message, context),
      error,
    }
    this.log(entry)
  }

  critical(message: string, error?: Error, context?: LogContext): void {
    const entry = {
      ...this.createBaseEntry(LogLevel.CRITICAL, message, context),
      error,
    }
    this.log(entry)
  }
}

/**
 * Create a logger instance with the provided configuration
 */
export const createLogger = (config: LoggerConfig): Logger => {
  return new LoggerImpl(config)
}
